import { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { api } from "../api/axios";
import { useI18n } from "../../i18n/I18nContext";

function SkeletonRow({ cols = 9 }) {
  return (
    <tr className="border-t">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="p-3">
          <div className="h-4 w-full max-w-[220px] animate-pulse rounded bg-gray-200" />
        </td>
      ))}
    </tr>
  );
}

export default function Orders() {
  const { t } = useI18n();
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [noticeMsg, setNoticeMsg] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdminView = location.pathname.startsWith("/admin");

  const translations = {
    pending: t("Pendente"),
    processing: t("Em Processamento"),
    pending_payment: t("Aguarda Pagamento"),
    paid: t("Pago"),
    shipped: t("Enviado"),
    delivered: t("Entregue"),
    cancelled: t("Cancelada"),
    refunded: t("Reembolsada"),
  };

  const getOrdersEndpoint = (page = 1) => {
    return isAdminView
      ? `/api/admin/orders?page=${page}`
      : `/api/v1/orders?page=${page}`;
  };

  const loadOrders = async (page = 1) => {
    setLoading(true);
    setErrorMsg("");

    try {
      const res = await api.get(getOrdersEndpoint(page));

      setOrders(res.data.data || []);
      setMeta(
        res.data.meta || {
          current_page: res.data.current_page || 1,
          last_page: res.data.last_page || 1,
        }
      );
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t("Erro a carregar encomendas."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [isAdminView]);

  useEffect(() => {
    if (isAdminView) return;

    const payment = searchParams.get("payment");
    const orderId = searchParams.get("order_id");

    if (!payment) return;

    const handlePaymentRedirect = async () => {
      try {
        if (payment === "cancelled" && orderId) {
          await api.post(`/api/v1/orders/${orderId}/cancel-unpaid`);
          setNoticeMsg(t("Pagamento cancelado. A encomenda foi cancelada."));
        } else if (payment === "success" && orderId) {
          setNoticeMsg(t("Pagamento concluído com sucesso."));
        }

        await loadOrders();
      } catch (err) {
        setErrorMsg(
          err?.response?.data?.message ||
            t("Ocorreu um problema ao atualizar o estado da encomenda.")
        );
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete("payment");
        next.delete("order_id");
        next.delete("session_id");
        setSearchParams(next, { replace: true });
      }
    };

    handlePaymentRedirect();
  }, [searchParams, setSearchParams, isAdminView]);

  const goPrev = () => {
    if (meta.current_page > 1) loadOrders(meta.current_page - 1);
  };

  const goNext = () => {
    if (meta.current_page < meta.last_page) loadOrders(meta.current_page + 1);
  };

  const goToDetail = (id) => {
    navigate(isAdminView ? `/admin/orders/${id}` : `/orders/${id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-10">
      <div className="mx-auto max-w-7xl">
        <h1 className="mb-6 text-2xl font-bold text-gray-900">
          {isAdminView ? t("Gestão de Encomendas") : t("As Minhas Encomendas")}
        </h1>

        {noticeMsg && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {noticeMsg}
          </div>
        )}

        {errorMsg && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMsg}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                <tr>
                  <th className="p-3">{t("Nº Encomenda")}</th>

                  {isAdminView && (
                    <>
                      <th className="p-3">{t("Cliente")}</th>
                      <th className="p-3">{t("Email")}</th>
                    </>
                  )}

                  <th className="p-3">{t("Subtotal")}</th>
                  <th className="p-3">{t("Envio")}</th>
                  <th className="p-3">{t("Desconto")}</th>
                  <th className="p-3">{t("Total")}</th>
                  <th className="p-3">{t("Estado")}</th>
                  <th className="p-3">{t("Feita a")}</th>
                  <th className="p-3">{t("Paga a")}</th>
                  <th className="p-3 text-center">{t("Ações")}</th>
                </tr>
              </thead>

              <tbody className="text-gray-800">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <SkeletonRow key={i} cols={isAdminView ? 11 : 9} />
                  ))
                ) : orders.length > 0 ? (
                  orders.map((o) => (
                    <tr key={o.id} className="border-t hover:bg-gray-50/60">
                      <td className="p-3">{o.order_number}</td>

                      {isAdminView && (
                        <>
                          <td className="p-3">{o.user?.name || "-"}</td>
                          <td className="p-3">{o.user?.email || "-"}</td>
                        </>
                      )}

                      <td className="p-3">€ {o.amounts?.subtotal_amount ?? "0.00"}</td>
                      <td className="p-3">€ {o.amounts?.shipping_amount ?? "0.00"}</td>
                      <td className="p-3">€ {o.amounts?.discount_amount ?? "0.00"}</td>
                      <td className="p-3 font-bold">€ {o.amounts?.total_amount ?? "0.00"}</td>
                      <td className="p-3">{translations[o.status] || o.status}</td>
                      <td className="p-3">{o.placed_at || "-"}</td>
                      <td className="p-3">{o.paid_at || "-"}</td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() => goToDetail(o.id)}
                          className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-800"
                        >
                          {t("Ver Detalhes")}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="p-10 text-center"
                      colSpan={isAdminView ? 11 : 9}
                    >
                      <div className="mx-auto max-w-md">
                        <div className="text-lg font-semibold text-gray-900">
                          {t("Sem encomendas")}
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          {t("Ainda não há encomendas para mostrar.")}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {meta.last_page > 1 && (
          <div className="mt-4 flex items-center justify-center gap-4">
            <button
              onClick={goPrev}
              disabled={meta.current_page === 1}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t("← Anterior")}
            </button>

            <span className="text-sm text-gray-600">
              {t(`Página ${meta.current_page} de ${meta.last_page}`)}
            </span>

            <button
              onClick={goNext}
              disabled={meta.current_page === meta.last_page}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t("Próximo →")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}